import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import OfferCard from "@/components/OfferCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, Loader2, Search, ArrowLeft, ShieldCheck, Compass, Navigation } from "lucide-react";
import { useGeolocation } from "@/hooks/useGeolocation";
import { toast } from "sonner";
import { useSeo } from "@/hooks/useSeo";

const RADII = [1, 5, 10];
const FALLBACK_RADII = [1, 5, 10, 20, 30];

export default function AroundMe() {
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const geo = useGeolocation();
  const [intro, setIntro] = useState(!geo.coords); // affiche l'écran d'explication tant qu'on n'a pas de coords
  const [radius, setRadius] = useState(Number(sp.get("r")) || 5);
  const [q, setQ] = useState(sp.get("q") || "");
  const [category, setCategory] = useState(sp.get("category") || "");
  const [cats, setCats] = useState([]);
  const [cities, setCities] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scope, setScope] = useState("local"); // local | expanded | departement
  const [effRadius, setEffRadius] = useState(radius);
  useSeo({
    title: "Les bons plans autour de moi | PROXIreduc-28",
    description: "Géolocalisez instantanément les offres les plus proches en Eure-et-Loir. Inscription gratuite pour en profiter partout.",
    canonicalPath: "/autour-de-moi",
    image: "/og-autour-de-moi.jpg",
  });

  useEffect(() => {
    document.title = "Bons plans autour de moi | PROXIreduc-28";
    api.get("/categories").then((r) => setCats(r.data || []));
    api.get("/cities").then((r) => setCities(r.data || []));
  }, []);

  useEffect(() => {
    if (geo.coords) setIntro(false);
  }, [geo.coords]);

  const askLocation = async () => {
    const c = await geo.request();
    if (c) setIntro(false);
  };

  const load = async () => {
    if (!geo.coords) return;
    setLoading(true);
    const base = { near_lat: String(geo.coords.lat), near_lng: String(geo.coords.lng), limit: "40" };
    if (q) base.q = q;
    if (category) base.category = category;
    try {
      // Élargissement AUTOMATIQUE : on part du rayon choisi puis on élargit (5, 10, 20, 30 km)
      // jusqu'à trouver des offres — on ne laisse jamais une zone vide sans alternative.
      const radii = [radius, ...FALLBACK_RADII.filter((r) => r > radius)];
      let found = [], used = radius, sc = "local";
      for (const r of radii) {
        const p = new URLSearchParams({ ...base, radius_km: String(r) });
        const { data } = await api.get(`/offers?${p.toString()}`);
        if (data && data.length) { found = data; used = r; sc = r === radius ? "local" : "expanded"; break; }
      }
      // Dernier recours : tout l'Eure-et-Loir (sans géoloc), pour ne jamais rester vide.
      if (!found.length) {
        const p = new URLSearchParams({ limit: "40" });
        if (q) p.set("q", q);
        if (category) p.set("category", category);
        const { data } = await api.get(`/offers?${p.toString()}`);
        found = data || []; used = null; sc = "departement";
      }
      setOffers(found); setEffRadius(used); setScope(sc);
      // URL partageable SANS lat/lng (RGPD)
      const share = new URLSearchParams();
      share.set("r", String(radius));
      if (q) share.set("q", q);
      if (category) share.set("category", category);
      setSp(share, { replace: true });
    } catch { toast.error("Erreur de chargement."); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (geo.coords) load(); /* eslint-disable-next-line */ }, [geo.coords, radius, category]);

  const total = offers.length;
  const refused = geo.error && !geo.coords;

  // ======== Écran 1 : INTRO / demande d'autorisation ========
  if (intro && !geo.coords && !refused) {
    return (
      <div className="max-w-xl mx-auto px-4 py-10 md:py-14 text-center" data-testid="around-intro">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-orange-50 text-[#E05638] grid place-items-center">
          <MapPin size={28}/>
        </div>
        <h1 className="mt-4 font-heading text-2xl md:text-3xl font-extrabold text-slate-900" data-testid="around-title">
          Trouvez les bons plans près de vous
        </h1>
        <p className="mt-3 text-slate-600">
          Nous utilisons votre position <b>uniquement</b> pour rechercher les offres et professionnels situés à proximité.
        </p>
        <Button onClick={askLocation} disabled={geo.loading} className="mt-6 h-12 px-6 btn-shine text-white text-base" data-testid="around-authorize">
          {geo.loading ? <><Loader2 size={16} className="mr-2 animate-spin"/> Localisation...</> : <><MapPin size={16} className="mr-2"/> Autoriser ma position</>}
        </Button>
        <div className="mt-4 flex flex-wrap gap-2 justify-center items-center text-xs text-slate-500">
          <ShieldCheck size={12}/> Position ponctuelle, non conservée.
          <Link to="/confidentialite" className="underline hover:text-slate-700">Pourquoi ?</Link>
        </div>
        <div className="mt-10 pt-6 border-t border-slate-100 space-y-5 text-left">
          <div>
            <p className="text-sm text-slate-700 mb-2 font-semibold flex items-center gap-1.5">
              <Navigation size={14} className="text-[#E05638]" /> Pas de GPS ? Entrez votre code postal
            </p>
            <p className="text-xs text-slate-500 mb-2">Tri par distance précis, plus fin que le simple choix de la commune.</p>
            <PostalSelector onLocate={(c) => geo.setManual(c)} />
          </div>
          <div>
            <p className="text-sm text-slate-500 mb-2">Ou choisissez une commune</p>
            <CitySelector cities={cities} onPick={(name) => navigate(`/bons-plans?city=${encodeURIComponent(name)}`)}/>
          </div>
        </div>
      </div>
    );
  }

  // ======== Écran 2 : REFUS / erreur ========
  if (refused) {
    return (
      <div className="max-w-xl mx-auto px-4 py-10 md:py-14 text-center" data-testid="around-refused">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-amber-50 text-amber-600 grid place-items-center"><MapPin size={28}/></div>
        <h1 className="mt-4 font-heading text-2xl md:text-3xl font-extrabold text-slate-900">
          La localisation n'a pas été autorisée
        </h1>
        <p className="mt-3 text-slate-600">
          {geo.error || "Nous n'avons pas réussi à déterminer votre position."}
        </p>
        <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-5 text-left space-y-5">
          <div>
            <p className="text-sm text-slate-700 mb-2 font-semibold flex items-center gap-1.5">
              <Navigation size={14} className="text-[#E05638]" /> Entrez votre code postal
            </p>
            <p className="text-xs text-slate-500 mb-2">Pour un tri par distance précis sans géolocalisation.</p>
            <PostalSelector onLocate={(c) => geo.setManual(c)} />
          </div>
          <div>
            <p className="text-sm text-slate-500 mb-2">Ou choisissez une commune</p>
            <CitySelector cities={cities} onPick={(name) => navigate(`/bons-plans?city=${encodeURIComponent(name)}`)}/>
          </div>
        </div>
        <div className="mt-4">
          <Button onClick={askLocation} variant="outline" data-testid="around-retry">Réessayer la localisation</Button>
        </div>
      </div>
    );
  }

  // ======== Écran 3 : RÉSULTATS ========
  return (
    <div data-testid="around-results">
      <section className="bg-gradient-to-br from-emerald-50 via-white to-orange-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
          <div className="flex items-center justify-between gap-2 mb-2">
            <Link to="/" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900" data-testid="around-back">
              <ArrowLeft size={14}/> Recherche classique
            </Link>
            <button type="button" onClick={() => { geo.clear(); setOffers([]); setIntro(true); }}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#E05638] hover:underline" data-testid="around-change-location">
              <MapPin size={13}/> Changer de position
            </button>
          </div>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900 flex items-center gap-2">
                <MapPin className="text-[#E05638]" size={24}/> Bons plans autour de vous
              </h1>
              <p className="mt-1 text-slate-600 text-sm" data-testid="around-subtitle">
                {loading ? "Recherche en cours..." : scope === "local" ? (
                  <>Dans un rayon de <b>{radius} km</b> · <b>{total}</b> bon{total > 1 ? "s" : ""} plan{total > 1 ? "s" : ""} trouvé{total > 1 ? "s" : ""}</>
                ) : scope === "expanded" ? (
                  <>Rien à {radius} km — voici les <b>{total}</b> plus proches, jusqu'à <b>{effRadius} km</b></>
                ) : (
                  <>Rien à proximité — voici <b>{total}</b> bon{total > 1 ? "s" : ""} plan{total > 1 ? "s" : ""} dans tout l'Eure-et-Loir</>
                )}
              </p>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="around-radius-row">
              {RADII.map((r) => (
                <button key={r} onClick={() => setRadius(r)}
                        data-testid={`around-radius-${r}`}
                        className={`px-4 py-1.5 rounded-full text-sm font-bold border transition-colors ${
                          radius === r ? "bg-emerald-700 text-white border-emerald-700"
                                       : "bg-white border-emerald-200 text-emerald-800 hover:border-emerald-500"
                        }`}>{r} km</button>
              ))}
            </div>
          </div>

          <div className="mt-4 grid md:grid-cols-2 gap-3">
            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3">
              <Search size={16} className="text-slate-400 shrink-0"/>
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                     onKeyDown={(e) => { if (e.key === "Enter") load(); }}
                     placeholder="Que recherchez-vous ?"
                     className="border-0 shadow-none focus-visible:ring-0"
                     data-testid="around-search"/>
              <Button size="sm" onClick={load} data-testid="around-search-submit">OK</Button>
            </div>
            <Select value={category} onValueChange={(v) => setCategory(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="around-category" className="bg-white"><SelectValue placeholder="Toutes catégories"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes catégories</SelectItem>
                {cats.map((c) => (<SelectItem key={c.id} value={c.slug}>{c.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading ? (
          <div className="text-center py-14 text-slate-500 text-sm" data-testid="around-loading">
            <Loader2 size={28} className="mx-auto animate-spin text-slate-300 mb-2"/>
            Recherche des bons plans à proximité...
          </div>
        ) : total === 0 ? (
          <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-10 text-center" data-testid="around-empty">
            <Compass className="mx-auto text-slate-300" size={40}/>
            <div className="mt-3 text-slate-700 font-semibold">Aucun bon plan publié pour le moment.</div>
            <p className="mt-1 text-sm text-slate-500">De nouvelles offres locales arrivent régulièrement — revenez bientôt.</p>
            <div className="mt-4 flex flex-wrap gap-2 justify-center">
              <Button onClick={() => navigate("/bons-plans")} data-testid="around-empty-all">Voir tous les bons plans</Button>
              <div className="w-full mt-3">
                <p className="text-sm text-slate-600 mb-2">Ou choisissez une commune :</p>
                <CitySelector cities={cities} onPick={(name) => navigate(`/bons-plans?city=${encodeURIComponent(name)}`)}/>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="around-grid">
            {offers.map((o) => (<OfferCard key={o.id} offer={o}/>))}
          </div>
        )}
      </section>
    </div>
  );
}

function PostalSelector({ onLocate }) {
  const [pc, setPc] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!/^\d{5}$/.test(pc)) { toast.error("Entrez un code postal à 5 chiffres."); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/geocode-postal?postal_code=${pc}`);
      if (data?.lat != null && data?.lng != null) {
        onLocate({ lat: data.lat, lng: data.lng });
        toast.success(`Position fixée sur le ${pc}.`);
      } else { toast.error("Code postal introuvable."); }
    } catch { toast.error("Code postal introuvable."); }
    finally { setLoading(false); }
  };
  return (
    <div className="flex gap-2" data-testid="around-postal-row">
      <Input value={pc}
             onChange={(e) => setPc(e.target.value.replace(/\D/g, "").slice(0, 5))}
             onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
             inputMode="numeric" placeholder="Ex. 28000"
             className="bg-white" data-testid="around-postal-input"/>
      <Button disabled={loading || pc.length !== 5} onClick={submit}
              className="btn-shine text-white shrink-0" data-testid="around-postal-go">
        {loading ? <Loader2 size={16} className="animate-spin"/> : "Localiser"}
      </Button>
    </div>
  );
}

function CitySelector({ cities, onPick }) {
  const [val, setVal] = useState("");
  const active = cities.filter((c) => (c.offer_count || 0) + (c.business_count || 0) > 0);
  const list = active.length ? active : cities;
  return (
    <div className="flex gap-2">
      <Select value={val} onValueChange={setVal}>
        <SelectTrigger className="bg-white" data-testid="around-city-selector"><SelectValue placeholder="Choisir une commune..."/></SelectTrigger>
        <SelectContent>
          {list.map((c) => (<SelectItem key={c.id || c.name} value={c.name}>{c.name}</SelectItem>))}
        </SelectContent>
      </Select>
      <Button disabled={!val} onClick={() => onPick(val)} className="btn-shine text-white" data-testid="around-city-go">
        Y aller
      </Button>
    </div>
  );
}
